import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  Text,
  Button,
  InlineStack,
  Badge,
  Banner,
  Spinner,
  Box,
} from '@shopify/polaris';
import { getScanResults } from '../services/api';
import { trackScanCompleted } from '../services/analytics';
import { PageEntrance, AnimatedCounter } from '../components/ui';
import { fireConfetti } from '../components/ui/Confetti';
import { ScoreGauge } from '../components/ScoreGauge';
import { getScoreTierConfig } from '../theme/theme.config';
import type { ScanResultsResponse, IssueSummary, WinSummary } from '../types';
import './ScanCompletePage.css';

/**
 * T035: ScanCompletePage — scan completion reveal screen.
 * Shows overall score (animated counter), tier label, colour, "Based on N products",
 * top 3 issues, top 3 wins, and CTAs.
 */
export function ScanCompletePage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery<ScanResultsResponse>({
    queryKey: ['scan-results', jobId],
    queryFn: () => getScanResults(jobId!),
    enabled: !!jobId,
  });

  // T055: Track scan completed event + confetti for scores >= 70
  const confettiFired = useRef(false);
  useEffect(() => {
    if (data) {
      trackScanCompleted(0, data.productCount);
      if (data.overallScore >= 70 && !confettiFired.current) {
        confettiFired.current = true;
        // Delay confetti to let the score animation finish
        const timer = setTimeout(() => {
          const intensity = data.overallScore >= 90 ? 'full' : data.overallScore >= 80 ? 'medium' : 'light';
          fireConfetti(intensity);
        }, 1800);
        return () => clearTimeout(timer);
      }
    }
  }, [data]);

  if (isLoading) {
    return (
      <Page title="Scan Complete">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" gap="200">
                <Spinner size="small" />
                <Text as="p" variant="bodyMd">
                  Loading your results…
                </Text>
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (isError || !data) {
    return (
      <Page title="Scan Complete">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load results: {error instanceof Error ? error.message : 'Unknown error'}</p>
            </Banner>
            <Box paddingBlockStart="300">
              <Button onClick={() => navigate('/dashboard')}>Go to dashboard</Button>
            </Box>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <PageEntrance>
    <Page title="Your AI Discoverability Score">
      <Layout>
        {/* Zero products warning */}
        {data.productCount === 0 && (
          <Layout.Section>
            <Banner tone="warning">
              <p>
                No products were scanned. This can happen if your store has no published products
                or the app could not access your product data. Please check your app permissions
                and try rescanning.
              </p>
            </Banner>
            <Box paddingBlockStart="300">
              <InlineStack gap="200">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  Go to dashboard
                </Button>
              </InlineStack>
            </Box>
          </Layout.Section>
        )}

        {/* Score reveal — only show when products were actually scanned */}
        {data.productCount > 0 && (
          <Layout.Section>
            <div className="z-hero scancomp-hero">
              <div className="z-hero-content">
                <BlockStack gap="400" inlineAlign="center">
                  <ScoreGauge score={data.overallScore} colour={data.colour} size="hero" showGlow />

                  <AnimatedCounter
                    value={data.overallScore}
                    duration={1.4}
                    variant="heading2xl"
                    fontWeight="bold"
                    suffix="/100"
                    style={{
                      '--scancomp-score-color': getScoreTierConfig(data.overallScore).hex,
                      color: getScoreTierConfig(data.overallScore).hex,
                    } as React.CSSProperties}
                  />

                  <p className="scancomp-hero-tier-label">
                    {data.tierLabel}
                  </p>

                  <p className="z-hero-subtitle scancomp-hero-label">
                    {data.basedOnLabel}
                  </p>
                </BlockStack>
              </div>
            </div>
          </Layout.Section>
        )}

        {/* Sub-scores */}
        {data.productCount > 0 && (
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Score Breakdown
              </Text>
              <SubScoreRow label="Data Completeness" score={data.subScores.completeness} />
              <SubScoreRow label="Schema Quality" score={data.subScores.schema} />
              <SubScoreRow label="Identifiers" score={data.subScores.identifier} />
              <SubScoreRow label="Text Quality" score={data.subScores.textQuality} />
              <SubScoreRow label="Media Quality" score={data.subScores.media} />
              <SubScoreRow label="Consistency" score={data.subScores.consistency} />
            </BlockStack>
          </Card>
        </Layout.Section>
        )}

        {/* Top 3 Issues */}
        {data.topIssues.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Top Issues
                </Text>
                {data.topIssues.map((issue) => (
                  <IssueRow key={issue.title} issue={issue} />
                ))}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Top 3 Wins */}
        {data.topWins.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Quick Wins
                </Text>
                {data.topWins.map((win) => (
                  <WinRow key={win.title} win={win} />
                ))}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* CTAs */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Button variant="primary" fullWidth onClick={() => navigate('/dashboard')}>
                View full report
              </Button>
              <Button fullWidth onClick={() => navigate('/dashboard')}>
                Scan all products (Paid)
              </Button>
              <Button variant="plain" fullWidth onClick={() => navigate('/dashboard')}>
                Later
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
    </PageEntrance>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function SubScoreRow({ label, score }: Readonly<{ label: string; score: number }>) {
  let tone: 'success' | 'attention' | 'critical' = 'critical';
  if (score >= 70) tone = 'success';
  else if (score >= 50) tone = 'attention';
  return (
    <InlineStack align="space-between" blockAlign="center">
      <Text as="span" variant="bodyMd">
        {label}
      </Text>
      <Badge tone={tone}>
        {`${score}%`}
      </Badge>
    </InlineStack>
  );
}

function IssueRow({ issue }: Readonly<{ issue: IssueSummary }>) {
  return (
    <BlockStack gap="100">
      <InlineStack align="space-between" blockAlign="center">
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {issue.title}
        </Text>
        <Badge tone={getSeverityTone(issue.severity)}>
          {issue.severity}
        </Badge>
      </InlineStack>
      {issue.detail && (
        <Text as="p" variant="bodySm" tone="subdued">
          {issue.detail}
        </Text>
      )}
      <Text as="p" variant="bodySm" tone="subdued">
        {issue.affectedProductCount} product{issue.affectedProductCount === 1 ? '' : 's'} affected
      </Text>
    </BlockStack>
  );
}

function WinRow({ win }: Readonly<{ win: WinSummary }>) {
  return (
    <BlockStack gap="100">
      <InlineStack align="space-between" blockAlign="center">
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {win.title}
        </Text>
        <Badge tone="success">{`${win.estimatedImpact} impact`}</Badge>
      </InlineStack>
      {win.recommendedAction && (
        <Text as="p" variant="bodySm" tone="subdued">
          {win.recommendedAction}
        </Text>
      )}
      <Text as="p" variant="bodySm" tone="subdued">
        {win.affectedProductCount} product{win.affectedProductCount === 1 ? '' : 's'} affected
      </Text>
    </BlockStack>
  );
}

function getSeverityTone(severity: string): 'critical' | 'warning' | 'attention' | 'success' {
  switch (severity) {
    case 'CRITICAL': return 'critical';
    case 'HIGH': return 'warning';
    case 'MEDIUM': return 'attention';
    case 'LOW': return 'success';
    default: return 'attention';
  }
}
